/* eslint-disable prettier/prettier */
import { FlexColumnFitContent, FlexColumnSpaced, FlexRowSmallSpaced, SpaceBetweenRowGap } from '@fox/layout/src/flex';
import { DeviceType, useDeviceType } from '@fox/layout/src/use-device-type';
import { Card } from '@mui/material';
import React, { ReactNode } from 'react';
import styled, { CSSProperties } from 'styled-components';
import { ActionMenuPopover, ActionsMenuOption } from '../action-menu/index';

/**
 * @deprecated
 */
type BusinessCardProps = {
	/**
	 * The left hand highlight background color
	 * Think CSS string ie "#000000", "rgba(0,0,0) or "black"
	 * If undefined, no highlight will be shown
	 * @default undefined
	 */
	highlight?: string[];
	/**
	 * The Avatar to be displayed.
	 * Preference to use the <Avatar(Size)/> component in FoxD
	 * If undefined, no avatar will be shown
	 * @default undefined
	 */
	avatar?: JSX.Element;

	highlightWidth?: string;
	/**
	 * The menu component that should be shown in the top right of the component.
	 * If undefined, no menu will be shown
	 * @default undefined
	 */
	menu?: (() => React.ReactElement) | React.ReactElement;

	/**
	 * If true, indicates that the menu component is fully custom and should be rendered into
	 * the region reserved for the dropdown menu.
	 */
	menuIsCustom?: boolean;
} & React.HTMLAttributes<HTMLElement>;

type EntityBusinessCardProps = {
	/**
	 * The Avatar to be displayed.
	 * Preference to use the <Avatar(Size)/> component in FoxD
	 * If undefined, no avatar will be shown
	 * @default undefined
	 */
	avatar?: JSX.Element;

	children?: ReactNode;

	highlight?: string[];

	highlightWidth?: string;

	menuOptions?: ActionsMenuOption[] | ReactNode | undefined | null;

	title?: ReactNode;

	style?: React.CSSProperties;
};

export const BUSINESS_CARD_HEADER_HEIGHT = '3.75rem';

export const HighlightedCardContainer = styled(Card)<{ $highlightWidth?: string; $borderRadius?: CSSProperties['borderRadius'] }>`
	display: grid;
	grid-template-areas: 'lefthighlight content';
	grid-template-columns: ${p => p.$highlightWidth ?? '10px'} 1fr;
	height: 100%;
	max-width: 100%;
	min-height: 105px;
	row-gap: 0.5rem;
	${props => props.$borderRadius && `border-radius: ${props.$borderRadius};`}
`;

export const BusinessCardHighlight = styled.div<{ $highlight: string[] }>`
	grid-area: lefthighlight;
	height: 100%;
	position: relative;
	background: ${props =>
		props.$highlight?.length > 1
			? `linear-gradient(180deg${props.$highlight.map(h => ', ' + h).join('')} )`
			: props.$highlight?.length === 1
				? `linear-gradient(180deg, ${props.$highlight[0]}, ${props.$highlight[0]})`
				: ''};
`;

/**
 * @deprecated use EntityBusinessCard instead
 */
export const BusinessCardContent = styled.div`
	max-height: 100%;
	grid-area: content;
	left: calc(25px + 20%);
	margin: 12px 30px 14px 3em;
	text-overflow: ellipsis;
	overflow: hidden;

	a {
		color: inherit;
	}
`;

export const BusinessCardGrid = styled(Card)`
	height: 100%;
	max-width: 100%;
	display: flex;
	flex-direction: column;
	padding: 1rem;

	&:hover .actionMenu {
		opacity: 1;
	}
`;

const BusinessCardContainer = styled(FlexColumnSpaced)`
	padding: 1rem 1.5rem 1rem 1rem;
	&:hover .actionMenu {
		opacity: 1;
	}
`;

/**
 * @deprecated no longer in use
 */
export const BusinessCardMenu = styled.div`
	position: absolute;
	right: 2px;
	margin-right: 10px;
	margin-top: 7px;
`;
/**
 * @deprecated use EntityBusinessCard instead
 */
export const HighlightCard = (props: BusinessCardProps) => {
	const { highlight, avatar, children, menu, menuIsCustom, ...rest } = props;
	return (
		<HighlightedCardContainer id="businessCard" {...rest}>
			{highlight && <BusinessCardHighlight $highlight={highlight}>{avatar && avatar}</BusinessCardHighlight>}
			<BusinessCardContent>{children}</BusinessCardContent>
		</HighlightedCardContainer>
	);
};

export enum BusinessCardMode {
	Edit = 'edit',
	Create = 'create',
	View = 'view'
}

const AnimatedMenu = styled.div`
	opacity: 0;
	transition: opacity 0.25s ease-in-out;
	position: relative;
`;

/**
 * A wrapper for any items inside the business card header component so that they all match the height of the avatar
 */
export const BusinessCardHeaderWrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: ${BUSINESS_CARD_HEADER_HEIGHT};
	justify-content: center;
`;

export const EntityBusinessCard = (props: EntityBusinessCardProps) => {
	const { avatar, children, menuOptions, title, highlight, highlightWidth, ...rest } = props;
	const deviceType = useDeviceType();
	return (
		<>
			<HighlightedCardContainer
				elevation={2}
				$borderRadius={deviceType === DeviceType.Mobile ? '0px' : undefined}
				$highlightWidth={highlightWidth}
				{...rest}
			>
				{highlight ? <BusinessCardHighlight $highlight={highlight} /> : null}
				<BusinessCardContainer>
					{title && (
						<SpaceBetweenRowGap>
							<FlexRowSmallSpaced style={{ alignItems: 'center' }}>
								<FlexColumnFitContent>{avatar}</FlexColumnFitContent>
								<FlexColumnFitContent>{title}</FlexColumnFitContent>
							</FlexRowSmallSpaced>
							{menuOptions ? (
								Array.isArray(menuOptions) ? (
									<FlexColumnFitContent style={{ margin: '0 0 0 auto' }}>
										<BusinessCardHeaderWrapper>
											<AnimatedMenu className="actionMenu">
												<ActionMenuPopover options={menuOptions} />
											</AnimatedMenu>
										</BusinessCardHeaderWrapper>
									</FlexColumnFitContent>
								) : (
									menuOptions
								)
							) : null}
						</SpaceBetweenRowGap>
					)}
					{children}
				</BusinessCardContainer>
			</HighlightedCardContainer>
		</>
	);
};
